import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface NewsItem {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  image?: string;
  featured?: boolean;
  tags?: string[];
}

const NewsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('all');

  const newsItems: NewsItem[] = [
    {
      id: '1',
      title: 'Developer Marketplace Launches New AI Tools Category',
      excerpt: 'We\'re excited to announce our newest category dedicated to cutting-edge AI development tools and solutions. Browse hundreds of new listings to enhance your applications with artificial intelligence capabilities.',
      date: 'June 15, 2023',
      category: 'product',
      image: 'https://via.placeholder.com/600x400',
      featured: true,
      tags: ['AI', 'Machine Learning', 'New Category']
    },
    {
      id: '2',
      title: 'Upcoming Platform Maintenance: June 25, 2023',
      excerpt: 'We will be performing scheduled maintenance on our platform from 2:00 AM to 4:00 AM UTC on June 25. During this time, the marketplace may experience brief periods of downtime.',
      date: 'June 14, 2023',
      category: 'maintenance',
      tags: ['Maintenance', 'Downtime']
    },
    {
      id: '3',
      title: 'Developer Spotlight: Meet Sarah Chen, Creator of DevFlow',
      excerpt: 'This month, we\'re highlighting Sarah Chen, whose workflow automation tool DevFlow has helped thousands of developers streamline their processes. Learn about her journey and tips for success.',
      date: 'June 10, 2023',
      category: 'community',
      image: 'https://via.placeholder.com/600x400',
      tags: ['Developer Spotlight', 'Success Story']
    },
    {
      id: '4',
      title: 'New Payment Processing Options Available',
      excerpt: 'We\'ve expanded our payment options to include Apple Pay and Google Pay, making it easier for users to purchase tools and services with their preferred payment method.',
      date: 'June 8, 2023',
      category: 'product',
      tags: ['Payments', 'Feature Update']
    },
    {
      id: '5',
      title: 'Security Update: Enhanced Authentication Measures',
      excerpt: 'We\'ve implemented additional security features including two-factor authentication and improved session management to better protect your account.',
      date: 'June 5, 2023',
      category: 'security',
      tags: ['Security', '2FA', 'Authentication']
    },
    {
      id: '6',
      title: 'Community Hackathon Announced for July',
      excerpt: 'Join us for a virtual hackathon next month where developers can showcase their skills, collaborate on projects, and win prizes. Registration opens next week.',
      date: 'June 3, 2023',
      category: 'community',
      image: 'https://via.placeholder.com/600x400',
      tags: ['Hackathon', 'Community Event']
    },
    {
      id: '7',
      title: 'API Version 2.1 Released with New Endpoints',
      excerpt: 'Our latest API update includes new endpoints for better integration with third-party services and improved documentation to make implementation easier.',
      date: 'May 30, 2023',
      category: 'developer',
      tags: ['API', 'Developer Tools']
    },
    {
      id: '8',
      title: 'Seller Fee Structure Changes Coming in July',
      excerpt: 'Starting July 1, we\'re updating our fee structure to offer better rates for high-volume sellers and introducing volume-based discounts.',
      date: 'May 28, 2023',
      category: 'policy',
      tags: ['Fees', 'Seller Information']
    }
  ];

  const filteredNews = activeTab === 'all'
    ? newsItems
    : newsItems.filter(item => item.category === activeTab);

  const featuredNews = newsItems.find(item => item.featured);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">Developer Marketplace News</h1>
        <p className="text-[var(--color-muted)] mb-8">
          Stay updated with the latest announcements, features, and community highlights
        </p>

        {featuredNews && (
          <div className="bg-gradient-to-r from-[#7F56D9]/20 to-[#F670C7]/20 rounded-lg p-6 mb-8">
            <div className="flex flex-col md:flex-row gap-6">
              {featuredNews.image && (
                <div className="md:w-2/5">
                  <img
                    src={featuredNews.image}
                    alt={featuredNews.title}
                    className="w-full h-full object-cover rounded-md"
                  />
                </div>
              )}
              <div className="md:w-3/5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-[#7F56D9] text-white px-2 py-0.5 rounded-full">
                    Featured
                  </span>
                  <span className="text-sm text-[var(--color-muted)]">
                    {featuredNews.date}
                  </span>
                </div>
                <h2 className="text-2xl font-bold mb-2">{featuredNews.title}</h2>
                <p className="text-[var(--color-muted)] mb-4">{featuredNews.excerpt}</p>
                <Link
                  to={`/news/${featuredNews.id}`}
                  className="text-[#7F56D9] hover:underline inline-flex items-center gap-1"
                >
                  Read full article
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="bg-[#1F1A27] rounded-lg overflow-hidden mb-8">
          <div className="flex overflow-x-auto">
            {['all', 'product', 'security', 'maintenance', 'community', 'developer', 'policy'].map((tab) => (
              <button
                key={tab}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                  activeTab === tab
                    ? 'text-[#7F56D9] border-b-2 border-[#7F56D9]'
                    : 'text-[var(--color-muted)] hover:text-white'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredNews.map((item) => (
            <div key={item.id} className="bg-[#1F1A27] rounded-lg overflow-hidden">
              {item.image && (
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-48 object-cover"
                />
              )}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-[#2A2633] text-[var(--color-muted)] px-2 py-0.5 rounded-full">
                    {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                  </span>
                  <span className="text-sm text-[var(--color-muted)]">
                    {item.date}
                  </span>
                </div>
                <h2 className="text-xl font-bold mb-2">{item.title}</h2>
                <p className="text-[var(--color-muted)] mb-4">{item.excerpt}</p>
                <div className="flex justify-between items-center">
                  <Link
                    to={`/news/${item.id}`}
                    className="text-[#7F56D9] hover:underline"
                  >
                    Read more
                  </Link>
                  {item.tags && (
                    <div className="flex gap-2">
                      {item.tags.slice(0, 2).map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs bg-[#2A2633] text-[var(--color-muted)] px-2 py-0.5 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-center">
          <button className="bg-[#2A2633] hover:bg-[#3A3643] text-white py-2 px-6 rounded-md transition-colors">
            Load More News
          </button>
        </div>

        <div className="mt-12 bg-[#1F1A27] rounded-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Subscribe to Our Newsletter</h2>
          <p className="text-[var(--color-muted)] mb-6">
            Get the latest updates delivered directly to your inbox
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              placeholder="Your email address"
              className="flex-grow py-3 px-4 bg-[#2A2633] border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7F56D9]"
            />
            <button className="btn-primary py-3 px-6">
              Subscribe
            </button>
          </div>
          <p className="text-xs text-[var(--color-muted)] mt-3">
            We respect your privacy. Unsubscribe at any time.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NewsPage;
